const SENSITIVE_QUERY_KEYS = new Set([
    'code',
    'request',
    'code_verifier',
    'code_challenge',
    'access_token',
    'refresh_token',
    'token_hash',
    'email',
    'login_hint',
    'preview_handoff',
    'lifecycle_handoff',
    'lifecycle_recovery',
    'preference_token',
    'token',
    'funnel_intent_id',
    'finalization_request_id',
]);

export type TelemetryQueryString =
    | string
    | Record<string, string>
    | Array<[string, string]>;

const SENTRY_BREADCRUMB_URL_KEYS = ['url', 'from', 'to'] as const;

/** Remove email/auth capabilities before a URL reaches telemetry or logs. */
export function sanitizeSensitiveUrl(rawUrl?: string): string | undefined {
    if (!rawUrl) {
        return undefined;
    }
    try {
        const isAbsolute = /^[a-z][a-z\d+.-]*:\/\//i.test(rawUrl);
        const url = new URL(rawUrl, 'https://paperzilla.invalid');
        SENSITIVE_QUERY_KEYS.forEach((key) => url.searchParams.delete(key));
        if (url.pathname.startsWith('/api/oauth/consent/')) { url.pathname = '/api/oauth/consent/[request]'; }

        const fragment = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
        const fragmentParams = new URLSearchParams(fragment);
        if ([...SENSITIVE_QUERY_KEYS].some((key) => fragmentParams.has(key))) {
            url.hash = '';
        }
        return isAbsolute
            ? url.toString()
            : `${url.pathname}${url.search}${url.hash}`;
    } catch {
        // Telemetry sanitization must fail closed. Returning an unparseable URL
        // could leak a capability that the parser was unable to remove.
        return undefined;
    }
}

/** Remove email/auth capabilities from Sentry's separately captured query data. */
export function sanitizeSensitiveQueryString(
    queryString?: TelemetryQueryString
): TelemetryQueryString | undefined {
    if (queryString === undefined) {
        return undefined;
    }

    if (typeof queryString === 'string') {
        try {
            const params = new URLSearchParams(
                queryString.startsWith('?') ? queryString.slice(1) : queryString
            );
            SENSITIVE_QUERY_KEYS.forEach((key) => params.delete(key));
            return params.toString() || undefined;
        } catch {
            return undefined;
        }
    }

    if (Array.isArray(queryString)) {
        const sanitized = queryString.filter(([key]) => !SENSITIVE_QUERY_KEYS.has(key));
        return sanitized.length > 0 ? sanitized : undefined;
    }

    const sanitized = Object.fromEntries(
        Object.entries(queryString).filter(([key]) => !SENSITIVE_QUERY_KEYS.has(key))
    );
    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

/** Mutate a Sentry-compatible request so URL and query fields share one policy. */
export function sanitizeSensitiveRequest(request: {
    url?: string;
    query_string?: TelemetryQueryString;
}): void {
    if (request.url) {
        request.url = sanitizeSensitiveUrl(request.url);
    }
    if (request.query_string !== undefined) {
        request.query_string = sanitizeSensitiveQueryString(request.query_string);
    }
}

/** Redact both network and navigation URL fields captured by Sentry breadcrumbs. */
export function sanitizeSensitiveBreadcrumbData(
    data?: Record<string, unknown>
): void {
    if (!data) {
        return;
    }

    SENTRY_BREADCRUMB_URL_KEYS.forEach((key) => {
        if (typeof data[key] === 'string') {
            data[key] = sanitizeSensitiveUrl(data[key]);
        }
    });
}
